// Module 11 — pure view-models for the v2 panels + selection (no DOM, no uPlot). All read selector
// outputs (selProsumers / selLoads / selSelectionDetail) and stay STABLE-EMPTY under a v1-shaped
// snapshot (no prosumers/loads ⇒ [] / zero counts), satisfying the dashboards.v1 invariant.
import type { Command, ProsumerRuntime, LoadRuntime, LoadServiceStatus, Snapshot } from "../domain/index";
import { asNodeId, asEdgeId } from "../domain/index";

export type BatteryMode = "charge" | "discharge" | "idle";

export interface SocRow {
  id: string;
  socPct: number;
  batteryMode: BatteryMode;
}

function batteryMode(batteryMw: number): BatteryMode {
  if (batteryMw > 0) return "discharge";
  if (batteryMw < 0) return "charge";
  return "idle";
}

export function prosumerSocRows(ps: readonly ProsumerRuntime[]): SocRow[] {
  return ps.map((p) => ({
    id: p.id as string,
    socPct: p.socFrac * 100,
    batteryMode: batteryMode(p.batteryMw),
  }));
}

export function formatSoc(rows: readonly SocRow[]): string {
  return rows.map((r) => `${r.id} ${r.socPct.toFixed(0)}% ${r.batteryMode}`).join(" · ");
}

export type LoadStatusCounts = Record<LoadServiceStatus, number>;

export function loadStatusCounts(ls: readonly LoadRuntime[]): LoadStatusCounts {
  const counts: LoadStatusCounts = { normal: 0, stressed: 0, shed: 0, dark: 0 };
  for (const l of ls) counts[l.status] += 1;
  return counts;
}

export function formatLoadCounts(counts: LoadStatusCounts): string {
  return `normal:${counts.normal} stressed:${counts.stressed} shed:${counts.shed} dark:${counts.dark}`;
}

// Always-visible system readout. The uPlot chart legends only show a value while the cursor hovers
// the plot (idle legends read "--"), which makes a live, running sim look frozen at a glance. This
// pure formatter turns the current snapshot's totals into one human line the mount renders every tick,
// so the headline numbers are visible without any interaction. Integer MW (no locale separators) keeps
// it deterministic under test. `undefined` (pre-first-snapshot) yields em-dash placeholders, never throws.
export function systemTotalsLine(snap: Snapshot | undefined): string {
  if (snap === undefined) return "tick — · demand — MW · gen —";
  const t = snap.totals;
  const marginal = snap.meritStack?.find((e) => e.isMarginal); // P13: the plant that set λ this tick
  const mw = (n: number): string => `${Math.round(n)}`;
  // Nuclear band uses the SAME formula as the generation chart (series.ts: nuclearMw ?? slackMw) so the
  // headline text and the GENERATION_STACK chart can never attribute different MW to "nuclear" on a tick.
  const nuke = t.nuclearMw ?? t.slackMw;
  // P16 C68: read the single-source otherGenMw (computed once in assembly); fallback for pre-v8 snapshots.
  const other = t.otherGenMw ?? (t.genMw - nuke - t.importMw - t.gtaGasMw);
  return (
    `tick ${snap.tick} · demand ${mw(t.grossDemandMw)} MW · served ${mw(t.grossDemandMw - (t.unservedMw ?? 0))} · ` +
    `gen ${mw(t.genMw)} (nuclear ${mw(nuke)} · import ${mw(t.importMw)} · ` +
    `gas ${mw(t.gtaGasMw)} · other ${mw(other)}) · ` +
    `curtailed ${mw(t.curtailedMw)} · unserved ${mw(t.unservedMw)} · reserve ${mw(t.reserveMw)}` +
    // v3.1 (audit 2.8): the system marginal COST λ = the cost of the marginal unit this hour ($0 in
    // surplus). It is NOT a market clearing PRICE (which would add scarcity rents / GA); label it as cost.
    (t.marginalCostPerMwh !== undefined ? ` · λ(mc) $${Math.round(t.marginalCostPerMwh)}/MWh` : "") +
    // F2 (RC-3): when the fleet can't serve demand, λ is lifted toward VOLL — surface the scarcity adder so the
    // shortage price is legible and not mistaken for a merit-cost spike (0/absent when served ⇒ nothing shown).
    (t.scarcityAdderPerMwh !== undefined && t.scarcityAdderPerMwh > 0 ? ` (scarcity +$${Math.round(t.scarcityAdderPerMwh)})` : "") +
    // P13: NAME the price-setting plant the engine already identified — demand-D made legible in the headline.
    (marginal !== undefined ? ` · margin: ${marginal.name} (${marginal.fuel})` : "") +
    // v4 (advanced engine): where the surplus goes + transmission loss + emissions intensity
    (t.exportMw !== undefined && t.exportMw > 0 ? ` · export ${mw(t.exportMw)}` : "") +
    (t.lossMw !== undefined ? ` · loss ${mw(t.lossMw)}` : "") +
    (t.emissionsKg !== undefined ? ` · CO₂ ${Math.round(t.emissionsKg / 1000)} t/h` : "") +
    // v4 role stack bands: base (nuclear/RoR/CHP flat) · follow (CCGT) · peak (OCGT) · vre · storage(net)
    (t.baseloadMw !== undefined
      ? ` · [base ${mw(t.baseloadMw)} · follow ${mw(t.loadFollowingMw ?? 0)} · ` +
        `peak ${mw(t.peakerMw ?? 0)} · vre ${mw(t.vreMw ?? 0)} · storage ${mw(t.storageNetMw ?? 0)}]`
      : "")
  );
}

export interface SelectionView {
  lines: string[];
}

// ── selection actions: "click a component, turn it off/on" (the vision's interactive testing) ────
export interface SelectionAction {
  label: string;
  command?: Command; // P4: a command-less action is an informational, NON-clickable label (e.g. static infrastructure)
}

// Pure: the snapshot's outages/faultsActive decide which single action the panel offers for the
// current pick. Nodes toggle (off ⇄ on, the FLISR test drive); REAL branches fault ⇄ clear.
// Synthetic overlay arc ids (`from→to`, layers.v1) are display-only — no fault control. Pre-snapshot
// (undefined) assumes energized/unfaulted, so the control is usable from tick 0 and never throws.
export function selectionActions(
  sel: { kind: "node" | "edge" | "gen" | "load"; id: string } | null | undefined,
  snap: Snapshot | undefined,
): SelectionAction[] {
  if (!sel) return [];
  if (sel.kind === "load") return []; // detail-only pick (no toggle semantics yet)
  // P4 (ontario-merge): a GENERATOR (plant) pick offers Shut down / Restart — the user's "plants can't shut
  // down" complaint. Mapped to toggling the plant's dedicated bus: out-of-GTA plants sit on `G_*_SITE` buses
  // with NO co-located load, so the bus-toggle cleanly drops just that unit (buildUnits skips an off bus).
  if (sel.kind === "gen") {
    const d = snap?.dispatch.find((x) => (x.genId as string) === sel.id);
    if (!d?.busId) return []; // pre-P4 snapshot without busId ⇒ detail-only (never offer a false control)
    const off = snap?.outages.some((id) => (id as string) === (d.busId as string)) ?? false;
    return [{ label: off ? "Restart plant" : "Shut down plant", command: { t: "toggleNode", id: d.busId, on: off } }];
  }
  if (sel.kind === "node") {
    // P4: a displayOnly node is STATIC INFRASTRUCTURE (an out-of-GTA OSM fragment never in the solve). Be
    // HONEST — show an informational label, NOT the old no-op "Turn off" that pretended it was a live control.
    if (snap?.displayOnlyNodes?.some((id) => (id as string) === sel.id)) {
      return [{ label: "Static infrastructure — no live feed" }];
    }
    const out = snap?.outages.some((id) => (id as string) === sel.id) ?? false;
    return [{
      label: out ? "Turn on" : "Turn off",
      command: { t: "toggleNode", id: asNodeId(sel.id), on: out },
    }];
  }
  if (sel.id.includes("→")) return []; // overlay arc — not an electrical branch
  // P-viz fix: a LINE (edge) is now turn-off-able — a planned outage (toggleEdge), the user's "lines can't
  // be turned off". Distinct from a FAULT (which also self-heals): "Turn off line" = take it out of service.
  const off = snap?.manualOffEdges?.some((id) => (id as string) === sel.id) ?? false;
  const faulted = snap?.faultsActive.some((f) => f.targetKind === "branch" && f.targetId === sel.id) ?? false;
  const fault = { targetKind: "branch" as const, targetId: sel.id };
  const actions: SelectionAction[] = [
    { label: off ? "Restore line" : "Turn off line", command: { t: "toggleEdge", id: asEdgeId(sel.id), on: off } },
  ];
  // only offer a fault on an in-service line (a line already out of service has nothing to fault)
  if (!off) actions.push({
    label: faulted ? "Clear fault" : "Fault line",
    command: faulted ? { t: "clearFault", fault } : { t: "injectFault", fault },
  });
  // REV-B: per-CONNECTION breakers — open/close just ONE END of the line (its connection to that facility's
  // bus), the V3 connections_map powerPort. "from-end" = the u endpoint, "to-end" = v (the datasheet shows the
  // named from→to). Opening either end isolates the line (carries no flow) while the other bus stays live.
  // Offered only on an in-service line (a whole-line outage makes the per-port control moot).
  if (!off) {
    const uOpen = snap?.openPorts?.some((p) => p === `${sel.id}|u`) ?? false;
    const vOpen = snap?.openPorts?.some((p) => p === `${sel.id}|v`) ?? false;
    actions.push({
      label: uOpen ? "Reconnect from-end" : "Disconnect from-end",
      command: { t: "togglePort", id: asEdgeId(sel.id), end: "u", closed: uOpen },
    });
    actions.push({
      label: vOpen ? "Reconnect to-end" : "Disconnect to-end",
      command: { t: "togglePort", id: asEdgeId(sel.id), end: "v", closed: vOpen },
    });
  }
  return actions;
}

// `detail` is the opaque selSelectionDetail output (Dispatch | BranchFlow | LoadRuntime | {id,angle}
// | undefined). Render its scalar fields as label lines; null when nothing is selected.
export function selectionView(detail: unknown): SelectionView | null {
  if (detail === null || detail === undefined || typeof detail !== "object") return null;
  const lines = Object.entries(detail as Record<string, unknown>).map(
    ([k, v]) => `${k}: ${String(v)}`,
  );
  return lines.length === 0 ? null : { lines };
}

// ── Phase 3.5 (P-viz): event log — a narrated "what happened" timeline. The map already shows CURRENT
// state (a fault glyph, a shed load, a split island), but nothing narrates the SEQUENCE ("tick 47:
// Darlington tripped → tick 52: restored") the way this project's own diagnosis called out. Pure
// STATE-TRANSITION detection: an event fires only on a CHANGE (a fault appears/clears, UFLS shed
// starts/stops, the live island count changes, unserved demand jumps past a noise floor) — never every
// tick a condition merely HOLDS, or a sustained fault would spam one line per tick forever.
export type LogEventKind = "fault" | "clear" | "shed-start" | "shed-clear" | "island-split" | "island-merge" | "unserved-spike";

export interface LogEvent {
  tick: number;
  kind: LogEventKind;
  text: string;
}

export interface EventLogState {
  readonly hasBaseline: boolean; // false only before the very first snapshot is seen
  readonly faultIds: ReadonlySet<string>;
  readonly shedActive: boolean;
  readonly islandCount: number;
  readonly unservedMw: number;
}

export const INITIAL_EVENT_LOG_STATE: EventLogState = {
  hasBaseline: false, faultIds: new Set(), shedActive: false, islandCount: 1, unservedMw: 0,
};

// Distinct LIVE islands (non-negative islandId values across all branches). Mirrors the same count the
// map's island-tint channel uses (src/viz/adapter.ts) — duplicated, not imported: dashboards may depend
// only on domain + state, never viz (CLAUDE.md boundary rule), and this is a 2-line derivation, not
// worth a cross-module abstraction. A pre-wire-bump or v1-shaped Snapshot has no islandId at all ⇒ 1
// (a healthy single island is the correct assumption when the data simply isn't there).
function liveIslandCount(snap: Snapshot): number {
  const ids = new Set<number>();
  for (const b of snap.branchFlows) if (b.islandId !== undefined && b.islandId >= 0) ids.add(b.islandId);
  return ids.size > 0 ? ids.size : 1;
}

const UNSERVED_SPIKE_MW = 200; // noise floor — ordinary tick-to-tick dispatch churn shouldn't narrate

export function deriveLogEvents(prev: EventLogState, snap: Snapshot): { next: EventLogState; events: LogEvent[] } {
  const tick = snap.tick;
  const faultIds = new Set(snap.faultsActive.map((f) => `${f.targetKind}:${f.targetId}`));
  const shedMw = snap.totals.uflsShedMw ?? 0;
  const shedActive = shedMw > 0;
  const islandCount = liveIslandCount(snap);
  const unservedMw = snap.totals.unservedMw;
  const next: EventLogState = { hasBaseline: true, faultIds, shedActive, islandCount, unservedMw };
  if (!prev.hasBaseline) return { next, events: [] }; // first snapshot establishes the baseline only — nothing to compare against yet

  const events: LogEvent[] = [];
  for (const id of faultIds) if (!prev.faultIds.has(id)) events.push({ tick, kind: "fault", text: `Fault: ${id}` });
  for (const id of prev.faultIds) if (!faultIds.has(id)) events.push({ tick, kind: "clear", text: `Cleared: ${id}` });
  if (shedActive && !prev.shedActive) events.push({ tick, kind: "shed-start", text: `UFLS shed began — ${Math.round(shedMw)} MW` });
  if (!shedActive && prev.shedActive) events.push({ tick, kind: "shed-clear", text: "UFLS shed cleared — load restored" });
  if (islandCount > prev.islandCount) events.push({ tick, kind: "island-split", text: `Grid split into ${islandCount} islands` });
  if (islandCount < prev.islandCount) events.push({ tick, kind: "island-merge", text: `Islands rejoined — ${islandCount} remaining` });
  if (unservedMw - prev.unservedMw >= UNSERVED_SPIKE_MW) {
    events.push({ tick, kind: "unserved-spike", text: `Unserved demand jumped to ${Math.round(unservedMw)} MW` });
  }
  return { next, events };
}
